import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fmtBRL, fmtDate, monthLabel } from '@/lib/format';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, ArrowDownLeft, ArrowUpRight, Circle, Paperclip, Download, X, Loader2, ChevronLeft as PrevIcon, ChevronRight as NextIcon } from 'lucide-react';
import { TL } from '@/constants/testIds';

function fmtSize(size) {
  if (!size) return '';
  if (size < 1024) return `${size} B`;
  return `${(size / 1024).toFixed(0)} KB`;
}

function isImage(a, ctype) {
  const m = (a?.mime_type || ctype || '');
  return m.startsWith('image/');
}

function isPdf(a, ctype) {
  const m = (a?.mime_type || ctype || '');
  return m === 'application/pdf';
}

function AttachmentPreviewDialog({ preview, onClose, onDownload, onPrev, onNext }) {
  const a = preview?.attachment;
  const url = preview?.url;
  const loading = preview?.loading;
  const error = preview?.error;
  const hasMany = (preview?.list || []).length > 1;

  return (
    <Dialog open={!!preview} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 min-w-0">
            <Paperclip className="h-4 w-4 text-emerald-500 shrink-0" />
            <span className="truncate">{a?.original_name || 'Comprovante'}</span>
            {a?.size ? <span className="text-xs font-normal text-muted-foreground shrink-0">{fmtSize(a.size)}</span> : null}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <div className="text-sm">Carregando comprovante…</div>
          </div>
        ) : error ? (
          <div className="py-12 text-center space-y-3">
            <div className="text-sm text-rose-500">Não foi possível carregar o arquivo.</div>
            <Button size="sm" variant="outline" onClick={() => a && onDownload(a, url)}>
              <Download className="h-4 w-4 mr-2" /> Tentar baixar
            </Button>
          </div>
        ) : url && a ? (
          <div className="space-y-3">
            {isImage(a) ? (
              <div className="rounded-lg border bg-black/40 overflow-hidden grid place-items-center max-h-[65vh]">
                <img src={url} alt={a.original_name} className="max-h-[65vh] w-auto object-contain" />
              </div>
            ) : isPdf(a) ? (
              <iframe src={url} title={a.original_name} className="w-full h-[65vh] rounded-lg border bg-white" />
            ) : (
              <div className="py-10 flex flex-col items-center gap-3 text-center">
                <Paperclip className="h-8 w-8 text-muted-foreground" />
                <div className="text-sm text-muted-foreground">Pré-visualização indisponível para este tipo de arquivo.</div>
              </div>
            )}
            {hasMany && (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <Button size="sm" variant="ghost" onClick={onPrev}><PrevIcon className="h-4 w-4" /> Anterior</Button>
                <span>{(preview.index ?? 0) + 1} de {preview.list.length}</span>
                <Button size="sm" variant="ghost" onClick={onNext}>Próximo <NextIcon className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        ) : null}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>
            <X className="h-4 w-4 mr-2" /> Fechar
          </Button>
          {a && (
            <Button variant="outline" onClick={() => onDownload(a, url)} disabled={!url}>
              <Download className="h-4 w-4 mr-2" /> Baixar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TimelinePage() {
  const now = new Date();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [preview, setPreview] = useState(null);

  const { data: cats } = useQuery({ queryKey: ['categories'], queryFn: async () => (await api.get('/categories')).data });
  const catMap = (cats || []).reduce((a, c) => (a[c.id] = c, a), {});

  const { data, isLoading } = useQuery({
    queryKey: ['timeline', ym],
    queryFn: async () => (await api.get('/timeline', { params: ym })).data,
  });

  const openPreview = (list, index) => {
    const attachment = list[index];
    if (!attachment) return;
    setPreview({ attachment, list, index, url: null, loading: true, error: false });
    api.get(`/attachments/${attachment.id}`, { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: r.headers['content-type'] || attachment.mime_type }));
        setPreview((p) => (p && p.attachment?.id === attachment.id ? { ...p, url, loading: false } : p));
      })
      .catch(() => {
        setPreview((p) => (p && p.attachment?.id === attachment.id ? { ...p, loading: false, error: true } : p));
      });
  };

  useEffect(() => {
    return () => { if (preview?.url) URL.revokeObjectURL(preview.url); };
  }, [preview?.url]);

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const stepPreview = (dir) => {
    if (!preview?.list?.length) return;
    const n = preview.list.length;
    const nextIndex = (preview.index + dir + n) % n;
    if (preview.url) URL.revokeObjectURL(preview.url);
    openPreview(preview.list, nextIndex);
  };

  const downloadCurrent = (a, url) => {
    const href = url || null;
    if (href) {
      const link = document.createElement('a');
      link.href = href; link.download = a.original_name || 'comprovante'; link.click();
      return;
    }
    api.get(`/attachments/${a.id}`, { responseType: 'blob' })
      .then((r) => {
        const u = URL.createObjectURL(new Blob([r.data]));
        const link = document.createElement('a');
        link.href = u; link.download = a.original_name || 'comprovante'; link.click();
        setTimeout(() => URL.revokeObjectURL(u), 1000);
      })
      .catch(() => {});
  };

  const prev = () => {
    const m = ym.month === 1 ? 12 : ym.month - 1;
    const y = ym.month === 1 ? ym.year - 1 : ym.year;
    setYm({ year: y, month: m });
  };
  const next = () => {
    const m = ym.month === 12 ? 1 : ym.month + 1;
    const y = ym.month === 12 ? ym.year + 1 : ym.year;
    setYm({ year: y, month: m });
  };

  return (
    <div data-testid={TL.page} className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Linha do tempo financeira</div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">{monthLabel(ym.year, ym.month)}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid={TL.prevMonth} variant="outline" size="sm" onClick={prev}><ChevronLeft className="h-4 w-4" /> Anterior</Button>
          <Button variant="outline" size="sm" onClick={() => setYm({ year: now.getFullYear(), month: now.getMonth() + 1 })}>Hoje</Button>
          <Button data-testid={TL.nextMonth} variant="outline" size="sm" onClick={next}>Próximo <ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {isLoading || !data ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-16 rounded-lg border bg-card/40 animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card data-testid={TL.balanceStart} className="p-4">
              <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Saldo inicial</div>
              <div className="mt-1 font-mono tabular-nums text-xl font-semibold">{fmtBRL(data.balance_start)}</div>
            </Card>
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Entradas</div>
              <div className="mt-1 font-mono tabular-nums text-xl font-semibold money-in">+{fmtBRL(data.income_total)}</div>
            </Card>
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Saídas</div>
              <div className="mt-1 font-mono tabular-nums text-xl font-semibold money-out">-{fmtBRL(data.expense_total)}</div>
            </Card>
            <Card data-testid={TL.balanceEnd} className="p-4">
              <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Saldo final</div>
              <div className="mt-1 font-mono tabular-nums text-xl font-semibold">{fmtBRL(data.balance_end)}</div>
            </Card>
          </div>

          {/* Timeline items */}
          {data.days.length === 0 ? (
            <Card className="p-12 text-center text-muted-foreground">Nenhum lançamento neste mês.</Card>
          ) : (
            <div className="relative pl-8 space-y-6">
              <div className="absolute left-[15px] top-2 bottom-2 w-px bg-border" />
              {data.days.map((g) => (
                <div key={g.date} data-testid={TL.dayGroup(g.date)}>
                  <div className="relative -ml-8 flex items-center gap-3 mb-3">
                    <div className="h-8 w-8 rounded-full bg-emerald-500 grid place-items-center shadow-md shadow-emerald-500/20 ring-4 ring-background">
                      <span className="text-[10px] font-mono font-bold text-white">{g.date.split('-')[2]}</span>
                    </div>
                    <div className="font-heading text-sm font-semibold">{fmtDate(g.date)}</div>
                  </div>
                  <div className="ml-0 space-y-2">
                    {g.items.map((it, idx) => (
                      <Card key={idx} className="p-3 card-elevate border-border/70">
                        <div className="flex items-center gap-3">
                          <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${
                            it.kind === 'PAYMENT' ? 'bg-blue-500/10 text-blue-500' :
                            it.type === 'INCOME' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'
                          }`}>
                            {it.kind === 'PAYMENT' ? <Circle className="h-4 w-4 fill-current" /> :
                              it.type === 'INCOME' ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {it.kind === 'PAYMENT' ? `Pagamento ${it.payment_method}` : it.description}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {it.kind === 'TRANSACTION' && catMap[it.category_id]?.name}
                              {it.kind === 'PAYMENT' && `Ref. transação`}
                            </div>
                          </div>
                          {(it.has_attachment || (it.attachment_count || 0) > 0) && (
                            <button
                              type="button"
                              onClick={() => openPreview(it.attachments, 0)}
                              className="hidden sm:flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-xs text-emerald-600 dark:text-emerald-400 shrink-0 hover:bg-emerald-500/20 transition-colors"
                              title={`Ver ${(it.attachments || []).map((a) => a.original_name).join(', ')}`}
                            >
                              <Paperclip className="h-3.5 w-3.5" />
                              <span className="font-medium">{it.attachment_count > 1 ? `${it.attachment_count} comprovantes` : 'Comprovante'}</span>
                            </button>
                          )}
                          <div className={`font-mono tabular-nums text-sm font-semibold shrink-0 ${
                            it.type === 'INCOME' ? 'money-in' : 'money-out'
                          }`}>
                            {it.type === 'INCOME' ? '+' : '-'}{fmtBRL(it.amount)}
                          </div>
                        </div>
                        {(it.attachments || []).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-border/50 flex flex-wrap gap-2">
                            {it.attachments.map((a, ai) => (
                              <button
                                key={a.id}
                                type="button"
                                onClick={() => openPreview(it.attachments, ai)}
                                className="inline-flex items-center gap-2 rounded-lg border border-border/70 bg-muted/30 hover:bg-muted/60 hover:border-emerald-500/40 px-2.5 py-1.5 text-xs text-left transition-colors max-w-full"
                                title={`Ver ${a.original_name}`}
                              >
                                <Paperclip className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                                <span className="truncate max-w-[180px] font-medium">{a.original_name}</span>
                                {a.size ? <span className="text-muted-foreground shrink-0">{fmtSize(a.size)}</span> : null}
                                <Download className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              </button>
                            ))}
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <AttachmentPreviewDialog
        preview={preview}
        onClose={closePreview}
        onDownload={downloadCurrent}
        onPrev={() => stepPreview(-1)}
        onNext={() => stepPreview(1)}
      />
    </div>
  );
}
